/**
 * WYSIWYG editor over the Canonical Document Model (Part 1.8 of the plan). Opens a template
 * version's stored CDM (docx-derived or otherwise), edits it as a real ProseMirror document via
 * cdmExtensions (schema.ts), and live-previews it as PDF via preview-canonical while editing.
 * Saving writes a new version through save-canonical. It is not live until promoted (the success
 * bar offers Promote to Live); this page never promotes on its own. In the Live workspace, which
 * is read-only, the route redirects to the template's detail page instead.
 *
 * Page setup, header/footer (all six DocSection slots), and footnote/endnote editing are all
 * available from the toolbar (PageSetupBtn.tsx, HeaderFooterBtn.tsx, FootnoteEndnoteBtn.tsx,
 * rendered inside CdmToolbar.tsx) rather than as side panels on this page itself. Table of
 * Contents (`toc`) is not editable in the canvas, per schema.ts's own design (it is a cached
 * render from source, not authored content).
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faFloppyDisk } from '@fortawesome/free-solid-svg-icons'
import { templatesApiService } from '../services/templatesApiService'
import { samplesApiService } from '../services/samplesApiService'
import { renderApiService } from '../services/renderApiService'
import { ApiError } from '../services/BaseApiService'
import { useCanonicalPreview } from '../hooks/useCanonicalPreview'
import { useDebounce } from '../hooks/useDebounce'
import { cdmExtensions } from '../cdm/schema'
import { Dropcursor } from '@tiptap/extensions'
import { BlockMove } from '../cdm/blockMove'
import { FieldDrop } from '../cdm/fieldInsert'
import { PaletteDrop } from '../blocks/paletteInsert'
import { PlaceholderEditing } from '../cdm/placeholderEdit'
import { ConditionalRunEditing } from '../cdm/conditionalRunEdit'
import { LoopRunEditing } from '../cdm/loopRunEdit'
import { collectImageBlobRefs, resolveImageSources } from '../cdm/imageSources'
import { cdmToDoc, type EditorDocument } from '../cdm/fromCdm'
import { docToCdm } from '../cdm/toCdm'
import { collectMonikers } from '../cdm/collectMonikers'
import { mergePayloadSkeleton } from '../utils/payloadSkeleton'
import PreviewDebounceBar, { PREVIEW_DEBOUNCE_KEYFRAMES } from '../components/editor/PreviewDebounceBar'
import { focusNearestPosition } from '../components/editor/focusNearestPosition'
import {
  CDM_SCHEMA_VERSION_CURRENT, defaultDocumentProperties, defaultDocSection, defaultParagraphFormat,
  type CanonicalDocument, type PageSetup,
} from '../cdm/types'
import type { Template } from '../types'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'
import { Button } from '../components/ui'
import PreviewPane from '../components/PreviewPane'
import CdmToolbar from '../components/editor/CdmToolbar'
import BlockDragHandle from '../components/editor/BlockDragHandle'
import FieldsPanel from '../components/editor/FieldsPanel'
import BlocksPanel from '../components/editor/BlocksPanel'
import TranslationsPanel from '../components/editor/TranslationsPanel'
import PlaceholderPopover from '../components/editor/PlaceholderPopover'
import ConditionalRunPopover from '../components/editor/ConditionalRunPopover'
import LoopRunPopover from '../components/editor/LoopRunPopover'
import BindingSetupDialog from '../components/editor/BindingSetupDialog'
import SaveBlockModal from '../components/editor/SaveBlockModal'
import {
  emptyInsertedNoteIds, pruneInsertedNotes, recordInsertedNotes, selectionBlockRange, setSaveBlockHandler,
  type BlockRange, type NoteSink,
} from '../blocks/teamBlocks'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { SecurityRole } from '../constants'
import { fitToWidthScale } from '../components/editor/canvasZoom'
import { useIsMobile } from '../hooks/useMediaQuery'
import PromoteToLiveModal from '../components/templates/PromoteToLiveModal'
import { useIsLiveWorkspace } from '../contexts/AppStateContext'

const PT_TO_PX = 4 / 3 // 96dpi / 72pt

// The main canvas only: the block builder's drag/move/drop behaviour. Header/footer and note
// editors keep plain cdmExtensions.
const editorExtensions = [
  ...cdmExtensions,
  Dropcursor.configure({ color: '#0F9D92', width: 2 }),
  BlockMove,
  FieldDrop,
  PaletteDrop,
  PlaceholderEditing,
  ConditionalRunEditing,
  LoopRunEditing,
]

/**
 * A brand-new template (opened via NewTemplatePage.tsx, which creates the Template shell but no
 * version yet) has no CDM to fetch — start from one section holding one empty paragraph, the same
 * minimal shape ensureNonEmptyBlocks (fromCdm.ts) would synthesize for an empty section body.
 */
function blankCanonicalDocument(): CanonicalDocument {
  return {
    schemaVersion: CDM_SCHEMA_VERSION_CURRENT,
    properties: defaultDocumentProperties(),
    sections: [{ ...defaultDocSection(), body: [{ $type: 'paragraph', format: defaultParagraphFormat(), inlines: [] }] }],
    monikers: [],
    footnotes: [],
    endnotes: [],
    comments: [],
  }
}

/**
 * How long edits settle before a preview is rendered. One constant for the document and the
 * payload, and the same value the countdown bar drains over — three places that have to agree or
 * the bar empties at the wrong moment.
 */
const PREVIEW_DEBOUNCE_MS = 800

export default function TemplateEditorPage() {
  const isLive = useIsLiveWorkspace()
  const { templateId } = useParams<{ templateId: string }>()
  // Live is read-only: nothing is edited there, only promoted versions are shown.
  if (isLive) return <Navigate to={templateId ? `/app/templates/${templateId}` : '/app/templates'} replace />
  return <TemplateEditor />
}

function TemplateEditor() {
  const navigate = useNavigate()
  const { templateId } = useParams<{ templateId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const versionIdOverride = searchParams.get('versionId') ?? undefined

  const [template, setTemplate] = useState<Template | null>(null)
  const [openedVersionId, setOpenedVersionId] = useState<string | null>(null)
  const [openedVersionNumber, setOpenedVersionNumber] = useState<number | null>(null)
  const [editorDocMeta, setEditorDocMeta] = useState<Omit<EditorDocument, 'pmDoc'> | null>(null)
  const [initialContent, setInitialContent] = useState<JSONContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [changeNote, setChangeNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [clashErrors, setClashErrors] = useState<Array<{ normalizedKey: string; conflictingSpellings: Array<{ rawName: string }> }> | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<{ templateVersionId: string; versionNumber: number } | null>(null)
  const [showPromote, setShowPromote] = useState(false)

  const [payloadJson, setPayloadJson] = useState('{}')
  const debouncedPayload = useDebounce(payloadJson, PREVIEW_DEBOUNCE_MS)

  const objectUrlsRef = useRef<string[]>([])
  const canvasScrollRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  // One side panel at a time: the editor and preview already share the width.
  const [sidePanel, setSidePanel] = useState<'fields' | 'blocks' | 'translations' | null>(null)
  const togglePanel = (p: 'fields' | 'blocks' | 'translations') => setSidePanel(cur => (cur === p ? null : p))
  // Fit-to-width by default, so a side panel or a narrow window never hides part of the page.
  const [zoomMode, setZoomMode] = useState<'fit' | 'actual'>('fit')
  const [canvasWidth, setCanvasWidth] = useState(0)
  const [pageHeight, setPageHeight] = useState(0)
  const pageRef = useRef<HTMLDivElement>(null)

  // Team block library: "Save as block…" (handle menu or the header button) and its outcome.
  const user = useCurrentUser()
  const canEditBlocks = !user || user.securityRole >= SecurityRole.EDITOR
  const [saveBlockRange, setSaveBlockRange] = useState<BlockRange | null>(null)
  const [teamBlocksVersion, setTeamBlocksVersion] = useState(0)
  const [blockNotice, setBlockNotice] = useState('')
  const [hasBlockSelection, setHasBlockSelection] = useState(false)
  // Notes/comments an inserted team block brought in. They join editorDocMeta; the ref mirrors it so two inserts in
  // flight number theirs past each other's, and the ids let the document drop them again once an undo orphans them.
  const docMetaRef = useRef(editorDocMeta)
  docMetaRef.current = editorDocMeta
  const insertedNotesRef = useRef(emptyInsertedNoteIds())
  const noteSink = useMemo<NoteSink>(() => ({
    current: () => docMetaRef.current ?? { footnotes: [], endnotes: [], comments: [] },
    add: notes => {
      const prev = docMetaRef.current
      if (!prev) return
      const next = {
        ...prev,
        footnotes: [...prev.footnotes, ...notes.footnotes],
        endnotes: [...prev.endnotes, ...notes.endnotes],
        comments: [...prev.comments, ...notes.comments],
      }
      docMetaRef.current = next
      recordInsertedNotes(insertedNotesRef.current, notes)
      setEditorDocMeta(next)
    },
  }), [])

  /**
   * The version whose content is currently in the editor.
   *
   * Saving rewrites ?versionId to the newly created version, which would otherwise re-run the
   * load effect, refetch, and remount the editor — losing the cursor on every save. This lets the
   * effect recognise "that version is already open" and do nothing.
   */
  const loadedVersionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!templateId) return
    // Already showing this version — the URL changed because we just saved, not because the user
    // asked for a different version.
    if (versionIdOverride && versionIdOverride === loadedVersionRef.current) return

    let cancelled = false
    setLoading(true)
    setLoadError(null)

    async function load() {
      try {
        const t = await templatesApiService.getById(templateId!)
        if (cancelled) return
        setTemplate(t)

        // The editor edits exactly the version it was given — it does not pick one.
        //
        // It used to fall back to `currentVersionId`, which is the ACTIVATED version, while this
        // page only ever saves drafts. A template saved here therefore reopened blank with the
        // work still in the database. Inferring "the newest version" instead only moved the
        // guess: the caller already knows which version the user clicked, so it passes it.
        //
        // No version and no versions on the template is the brand-new case (NewTemplatePage
        // creates the shell but no version) — a blank document is correct, and the first Save
        // creates version 1. No version but versions DO exist means the editor was reached
        // without a choice having been made; see below.
        const effectiveVersionId = versionIdOverride ?? null

        let templateVersionId: string | null = null
        let versionNumber: number | null = null
        let canonicalDocument: CanonicalDocument
        let samplePayload: string | null = null
        if (effectiveVersionId) {
          // A saved sample (a starter layout ships one) beats an all-empty-strings skeleton as the
          // preview payload. Best effort: no samples, or a failed list, keeps the skeleton.
          const [res, samples] = await Promise.all([
            templatesApiService.getCanonical(effectiveVersionId),
            samplesApiService.list(effectiveVersionId).catch(() => []),
          ])
          if (cancelled) return
          templateVersionId = res.templateVersionId
          versionNumber = res.versionNumber
          canonicalDocument = res.canonicalDocument
          samplePayload = samples[0]?.payloadJson ?? null
        } else {
          // Reached without a version — a bookmarked /edit URL, say. Showing a blank editor over
          // a template that HAS versions is the failure that started all this: it reads as "my
          // work is gone", and a subsequent save would fork a new version from nothing. Send the
          // user to the version list to choose instead of guessing on their behalf.
          const existing = await templatesApiService.listVersions(templateId!)
          if (cancelled) return
          if (existing.length > 0) {
            navigate(`/app/templates/${templateId}`, { replace: true })
            return
          }
          canonicalDocument = blankCanonicalDocument()
        }

        const edited = cdmToDoc(canonicalDocument)

        // Resolve every referenced image asset to an object URL before handing content to the
        // editor, so images display immediately rather than as broken-image placeholders. A blank
        // document has no images and no templateVersionId to resolve them against anyway.
        const blobRefs = new Set<string>()
        collectImageBlobRefs(edited.pmDoc, blobRefs)
        const urls = new Map<string, string>()
        if (templateVersionId) {
          await Promise.all([...blobRefs].map(async blobRef => {
            try {
              const blob = await templatesApiService.getAsset(templateVersionId!, blobRef)
              const url = URL.createObjectURL(blob)
              objectUrlsRef.current.push(url)
              urls.set(blobRef, url)
            } catch {
              // Asset missing/unreadable: the image node keeps its blobRef but shows no preview.
            }
          }))
        }
        if (cancelled) return

        loadedVersionRef.current = templateVersionId
        if (samplePayload !== null) setPayloadJson(samplePayload)
        setOpenedVersionId(templateVersionId)
        setOpenedVersionNumber(versionNumber)
        insertedNotesRef.current = emptyInsertedNoteIds()
        setEditorDocMeta({ schemaVersion: edited.schemaVersion, properties: edited.properties, footnotes: edited.footnotes, endnotes: edited.endnotes, comments: edited.comments, localization: edited.localization ?? null })
        setInitialContent(resolveImageSources(edited.pmDoc, urls))
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Failed to load template')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, versionIdOverride])

  useEffect(() => {
    return () => { objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u)) }
  }, [])

  const editor = useEditor(
    initialContent ? { extensions: editorExtensions, content: initialContent } : { extensions: editorExtensions },
    [initialContent]
  )

  useEffect(() => {
    if (!editor || !canEditBlocks) return
    setSaveBlockHandler(editor, setSaveBlockRange)
    const track = () => setHasBlockSelection(selectionBlockRange(editor.state) !== null)
    editor.on('selectionUpdate', track)
    editor.on('update', track)
    return () => {
      setSaveBlockHandler(editor, null)
      editor.off('selectionUpdate', track)
      editor.off('update', track)
    }
  }, [editor, canEditBlocks])

  useEffect(() => {
    if (!blockNotice) return
    const t = window.setTimeout(() => setBlockNotice(''), 5000)
    return () => window.clearTimeout(t)
  }, [blockNotice])

  function saveSelectionAsBlock() {
    const range = editor ? selectionBlockRange(editor.state) : null
    if (range) setSaveBlockRange(range)
  }

  const currentDocument = useCallback(() => {
    if (!editor || !editorDocMeta) return null
    return pruneInsertedNotes(docToCdm({ pmDoc: editor.getJSON(), ...editorDocMeta }), insertedNotesRef.current)
  }, [editor, editorDocMeta])

  // The preview's render locale: '' renders as the API would with no locale in the request.
  const [previewLocale, setPreviewLocale] = useState('')
  const previewFn = useCallback(
    (doc: Parameters<typeof renderApiService.previewCanonical>[0], payload: string, signal: AbortSignal) =>
      renderApiService.previewCanonical(doc, payload, { signal, templateVersionId: openedVersionId ?? undefined, templateId: templateId ?? undefined, locale: previewLocale || undefined }),
    [openedVersionId, templateId, previewLocale]
  )
  const { pdfUrl, loading: previewLoading, error: previewError, trigger } = useCanonicalPreview(previewFn)

  const [editVersion, setEditVersion] = useState(0)
  useEffect(() => {
    if (!editor) return
    const bump = () => setEditVersion(v => v + 1)
    editor.on('update', bump)
    return () => { editor.off('update', bump) }
  }, [editor])

  // The document edit is debounced, not only the payload. It previously was not, so every
  // keystroke fired a full PDF render — one request per character, each aborting the last, and
  // enough sustained typing to exceed the authenticated `render` rate limit of 60/min.
  const debouncedEditVersion = useDebounce(editVersion, PREVIEW_DEBOUNCE_MS)

  // A render is queued but not yet sent. Drives the countdown bar, so the editor can show that
  // the edit registered — debouncing without that feedback just reads as lag.
  const previewPending = debouncedEditVersion !== editVersion

  useEffect(() => {
    const doc = currentDocument()
    if (!doc) return
    trigger(doc, debouncedPayload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEditVersion, debouncedPayload, currentDocument, previewLocale])

  // Keep the preview payload in step with the variables actually in the document. The panel used
  // to be an empty textarea that nothing ever wrote to, so inserting a variable — or typing one —
  // left the user to work out its JSON key themselves, and the preview rendered every variable as
  // missing until they did.
  //
  // Additive only (see mergePayloadSkeleton): typed values survive, and a deleted variable leaves
  // its key rather than having the data pulled out from under the user.
  useEffect(() => {
    const doc = currentDocument()
    if (!doc) return
    setPayloadJson(current => mergePayloadSkeleton(current, collectMonikers(doc)))
    // Deliberately on the RAW edit version, not the debounced one: this is local work with no
    // request behind it, and a variable should appear in the payload panel the moment it is
    // typed rather than waiting out the preview's debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVersion, currentDocument])

  async function handleSave(isDraft: boolean) {
    if (!templateId || !template) return
    const doc = currentDocument()
    if (!doc) return
    setSaving(true)
    setSaveError(null)
    setClashErrors(null)
    setSaveSuccess(null)
    try {
      const result = await templatesApiService.saveCanonical(templateId, openedVersionId, doc, changeNote.trim() || undefined, isDraft)
      setSaveSuccess(result)
      loadedVersionRef.current = result.templateVersionId
      setOpenedVersionId(result.templateVersionId)
      setOpenedVersionNumber(result.versionNumber)

      // Point the URL at the version just created. Saving forks a new version, so without this a
      // reload would reopen the one that was being edited BEFORE the save — the editor would be
      // faithfully showing the version its URL names, and the user would see their save missing.
      // `replace` so Back still goes where the user came from rather than through each save.
      setSearchParams({ versionId: result.templateVersionId }, { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.detail && typeof err.detail === 'object' && 'clashErrors' in (err.detail as object)) {
        setClashErrors((err.detail as { clashErrors: typeof clashErrors }).clashErrors)
      } else if (err instanceof ApiError && err.status === 409) {
        setSaveError('This template was changed elsewhere since you opened it. Reload the page to see the latest version before saving.')
      } else {
        setSaveError(err instanceof ApiError ? err.message : 'Save failed')
      }
    } finally {
      setSaving(false)
    }
  }

  // Each section is laid out at its own page width and side margins (the section node's CSS
  // variables, see schema.ts Section), so the page is as wide as the widest one; the top margin is
  // the first section's and the bottom the last's.
  const pages = useMemo(
    () => (editor?.getJSON().content ?? []).map(s => (s as JSONContent).attrs?.page as PageSetup | undefined).filter((p): p is PageSetup => !!p),
    [editor, editVersion],
  )
  const pageWidthPx = pages.length ? Math.round(Math.max(...pages.map(p => p.widthPt)) * PT_TO_PX) : 816
  const marginPx = {
    top: pages.length ? pages[0].margins.top * PT_TO_PX : 72,
    bottom: pages.length ? pages[pages.length - 1].margins.bottom * PT_TO_PX : 72,
  }

  const fitScale = fitToWidthScale(canvasWidth, pageWidthPx)
  const scale = zoomMode === 'fit' ? fitScale : 1

  // The scroll container's content width, and the page's unscaled height (a transform does not
  // change layout size, so the frame around the page is sized to the scaled box by hand).
  useEffect(() => {
    const el = canvasScrollRef.current
    const pageEl = pageRef.current
    if (!el || !pageEl || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const cs = getComputedStyle(el)
      setCanvasWidth(el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0))
      setPageHeight(pageEl.offsetHeight)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    ro.observe(pageEl)
    return () => ro.disconnect()
  }, [loading, loadError])

  if (loading) return <div style={{ padding: spacing.lg, color: colors.textSecondary }}>Loading…</div>
  if (loadError) {
    return (
      <div style={{ padding: spacing.lg }}>
        <div style={{ color: colors.error, marginBottom: spacing.md }}>{loadError}</div>
        <Button variant="secondary" onClick={() => navigate(`/app/templates/${templateId}`)}>Back</Button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        ${PREVIEW_DEBOUNCE_KEYFRAMES}
        .cdm-editor .ProseMirror { outline: none; }
        .cdm-page section[data-cdm-section] { width: var(--cdm-page-w); padding: 0 var(--cdm-mr) 0 var(--cdm-ml); box-sizing: border-box; }
        .cdm-page section[data-cdm-section] + section[data-cdm-section] { margin-top: 24px; padding-top: var(--cdm-mt); border-top: 1px dashed ${colors.border}; }
        .cdm-editor .ProseMirror h1 { font-size: 1.6em; font-weight: 700; margin: 0.4em 0; }
        .cdm-editor .ProseMirror h2 { font-size: 1.3em; font-weight: 700; margin: 0.4em 0; }
        .cdm-editor .ProseMirror h3 { font-size: 1.1em; font-weight: 700; margin: 0.4em 0; }
        .cdm-editor .ProseMirror p { margin: 0.25em 0; }
        .cdm-editor .ProseMirror ul, .cdm-editor .ProseMirror ol { list-style: none; padding-left: 0; }
        .cdm-editor .ProseMirror li[data-cdm-list-item] { margin: 0.2em 0; }
        .cdm-editor .ProseMirror table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
        .cdm-editor .ProseMirror th, .cdm-editor .ProseMirror td { border: 1px solid #e2e8f0; padding: 6px 10px; vertical-align: top; min-width: 60px; }
        .cdm-editor .ProseMirror th { background: #f8fafc; font-weight: 600; }
        .cdm-editor .ProseMirror img { max-width: 100%; height: auto; }
        .cdm-editor .ProseMirror a[data-cdm-hyperlink] { color: #0F9D92; text-decoration: underline; }
        .cdm-editor .ProseMirror span[data-cdm-placeholder] { background: #eef2ff; color: #4338ca; border-radius: 3px; padding: 0 3px; font-family: monospace; cursor: pointer; }
        .cdm-editor .ProseMirror span[data-cdm-placeholder].ProseMirror-selectednode { outline: 2px solid #6366f1; outline-offset: 1px; }
        .cdm-editor .ProseMirror span[data-cdm-conditional-run] { cursor: pointer; }
        .cdm-editor .ProseMirror span[data-cdm-conditional-run].ProseMirror-selectednode { outline: 2px solid #7c3aed; outline-offset: 1px; }
        .cdm-editor .ProseMirror div[data-cdm-dyn-image] { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; max-width: 100%; box-sizing: border-box; overflow: hidden; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; color: ${colors.textMuted}; font-size: 0.8em; text-align: center; padding: 4px; }
        .cdm-editor .ProseMirror div[data-cdm-dyn-image] span { font-family: monospace; overflow-wrap: anywhere; }
        .cdm-editor .ProseMirror div[data-cdm-dyn-image].ProseMirror-selectednode { outline: 2px solid #0F9D92; }
        .cdm-editor .ProseMirror [data-cdm-floating] { outline: 1px dashed #0F9D92; outline-offset: 2px; cursor: help; }
        .cdm-editor .ProseMirror div[data-cdm-loop], .cdm-editor .ProseMirror div[data-cdm-conditional] { border: 1px dashed #94a3b8; border-radius: 4px; padding: 4px 8px; margin: 4px 0; }
        .cdm-editor .ProseMirror img[src=""], .cdm-editor .ProseMirror img:not([src]) { display: inline-block; outline: 1px dashed #94a3b8; background: #f8fafc; color: ${colors.textMuted}; font-size: 0.8em; }
        .cdm-editor .ProseMirror div[data-cdm-page-break] { border-top: 2px dashed ${colors.border}; margin: 12px 0; height: 0; }
        .cdm-editor .ProseMirror div[data-cdm-unknown-block], .cdm-editor .ProseMirror span[data-cdm-unknown-inline] { background: #fef2f2; color: #b91c1c; border-radius: 3px; padding: 0 3px; font-size: 0.85em; }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, padding: spacing.md, borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => navigate(`/app/templates/${templateId}`)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, fontSize: fontSize.base, cursor: 'pointer' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
        <div style={{ fontWeight: 600, color: colors.text }}>
          {template?.name} {openedVersionNumber != null && <span style={{ color: colors.textSecondary, fontWeight: 400 }}>v{openedVersionNumber}</span>}
        </div>
        <div style={{ flex: 1 }} />
        {(['blocks', 'fields', 'translations'] as const).map(p => (
          <button
            key={p}
            type="button"
            aria-pressed={sidePanel === p}
            onClick={() => togglePanel(p)}
            style={{ padding: '6px 12px', border: `1px solid ${sidePanel === p ? colors.primary : colors.border}`, borderRadius: radius.sm, background: sidePanel === p ? colors.primaryLight : 'none', color: sidePanel === p ? colors.primary : colors.textSecondary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}
          >
            {p === 'blocks' ? 'Blocks' : p === 'fields' ? 'Fields' : 'Translations'}
          </button>
        ))}
        {canEditBlocks && (
          <button
            type="button"
            onClick={saveSelectionAsBlock}
            disabled={!hasBlockSelection}
            title={hasBlockSelection ? 'Save the selected blocks to the team library' : 'Select content in the document to save it as a block'}
            style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: hasBlockSelection ? colors.textSecondary : colors.textMuted, fontSize: fontSize.sm, fontWeight: 600, cursor: hasBlockSelection ? 'pointer' : 'default', opacity: hasBlockSelection ? 1 : 0.6 }}
          >
            Save selection as block
          </button>
        )}
        {fitScale < 1 && (
          <button
            type="button"
            aria-pressed={zoomMode === 'fit'}
            onClick={() => setZoomMode(m => (m === 'fit' ? 'actual' : 'fit'))}
            title={zoomMode === 'fit' ? 'Show the page at 100%' : 'Fit the page to the available width'}
            style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, fontSize: fontSize.sm, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
          >
            {zoomMode === 'fit' ? `Fit width · ${Math.round(fitScale * 100)}%` : '100%'}
          </button>
        )}
        <input
          type="text"
          value={changeNote}
          onChange={e => setChangeNote(e.target.value)}
          placeholder="Change note"
          style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: 220 }}
        />
        <Button variant="primary" onClick={() => handleSave(true)} disabled={saving || !editor}>
          <FontAwesomeIcon icon={faFloppyDisk} style={{ marginRight: 6 }} />
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
      </div>

      {saveError && <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.errorLight, color: colors.error, fontSize: fontSize.sm }}>{saveError}</div>}
      {clashErrors && clashErrors.length > 0 && (
        <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.errorLight, color: colors.error, fontSize: fontSize.sm }}>
          Variable name clash: {clashErrors.map(c => c.conflictingSpellings.map(s => s.rawName).join(' vs. ')).join('; ')} — these must resolve to the same spelling.
        </div>
      )}
      {saveSuccess && (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.sm} ${spacing.md}`, background: colors.successLight, color: colors.success, fontSize: fontSize.sm }}>
          Saved as v{saveSuccess.versionNumber}.
          <Button variant="secondary" onClick={() => setShowPromote(true)}>
            {(template?.promotionApproval?.level ?? 'auto') !== 'auto' ? 'Request approval to promote…' : 'Promote to Live…'}
          </Button>
        </div>
      )}
      {blockNotice && (
        // Floating, so its arrival and departure never shift the panels under the pointer.
        <div role="status" style={{ position: 'fixed', bottom: spacing.lg, left: '50%', transform: 'translateX(-50%)', zIndex: 900, padding: `${spacing.sm} ${spacing.md}`, background: colors.successLight, color: colors.success, border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm, boxShadow: shadow.lg, fontSize: fontSize.sm }}>{blockNotice}</div>
      )}
      {saveBlockRange && editorDocMeta && (
        <SaveBlockModal
          range={saveBlockRange}
          meta={editorDocMeta}
          templateId={templateId!}
          templateVersionId={openedVersionId}
          onClose={() => setSaveBlockRange(null)}
          onSaved={message => {
            setSaveBlockRange(null)
            setTeamBlocksVersion(v => v + 1)
            setBlockNotice(message)
          }}
        />
      )}
      {showPromote && saveSuccess && (
        <PromoteToLiveModal
          version={saveSuccess}
          approvalLevel={template?.promotionApproval?.level ?? 'auto'}
          onClose={() => setShowPromote(false)}
          onDone={() => navigate(`/app/templates/${templateId}`)}
        />
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        {sidePanel && (
          <div
            style={isMobile
              ? { position: 'absolute', inset: 0, right: 'auto', width: 'min(320px, 85vw)', zIndex: 20, borderRight: `1px solid ${colors.border}`, boxShadow: shadow.lg }
              : { flex: `0 0 ${sidePanel === 'translations' ? 360 : 260}px`, minWidth: 0, borderRight: `1px solid ${colors.border}` }}
          >
            {sidePanel === 'translations'
              ? <TranslationsPanel
                  getDocument={currentDocument}
                  localization={editorDocMeta?.localization}
                  onChange={localization => setEditorDocMeta(prev => (prev ? { ...prev, localization } : prev))}
                  refreshKey={debouncedEditVersion}
                  canEdit={canEditBlocks}
                  onClose={() => setSidePanel(null)}
                  templateId={templateId}
                />
              : sidePanel === 'fields'
              ? <FieldsPanel editor={editor} templateId={templateId!} templateVersionId={openedVersionId} onClose={() => setSidePanel(null)} />
              : <BlocksPanel editor={editor} templateId={templateId!} versionId={openedVersionId} canEdit={canEditBlocks} teamRefreshKey={teamBlocksVersion} noteSink={noteSink} onClose={() => setSidePanel(null)} />}
          </div>
        )}
        <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', minWidth: 0, borderRight: `1px solid ${colors.border}` }}>
          <CdmToolbar
            editor={editor}
            templateId={templateId!}
            footnotes={editorDocMeta?.footnotes ?? []}
            endnotes={editorDocMeta?.endnotes ?? []}
            onFootnotesChange={footnotes => setEditorDocMeta(prev => (prev ? { ...prev, footnotes } : prev))}
            onEndnotesChange={endnotes => setEditorDocMeta(prev => (prev ? { ...prev, endnotes } : prev))}
          />
          {/* Block layout, not a centring flexbox: justify-content: center on an overflowing child
              pushes its left edge past the scroll origin, where it can never be scrolled to. The
              frame centres with auto margins instead, which collapse to 0 once the page is wider. */}
          <div ref={canvasScrollRef} data-testid="editor-canvas" style={{ flex: 1, overflow: 'auto', background: colors.background, padding: spacing.lg, position: 'relative' }}>
            <BlockDragHandle editor={editor} containerRef={canvasScrollRef} />
            <PlaceholderPopover editor={editor} />
            <ConditionalRunPopover editor={editor} />
            <LoopRunPopover editor={editor} />
            <BindingSetupDialog editor={editor} templateVersionId={openedVersionId} />
            <div
              data-testid="editor-page-frame"
              style={{
                width: Math.round(pageWidthPx * scale),
                height: scale < 1 && pageHeight ? Math.ceil(pageHeight * scale) : undefined,
                margin: '0 auto',
              }}
            >
              <div
                ref={pageRef}
                className="cdm-editor cdm-page"
                // A rendered hyperlinkRun is a real <a href> (see schema.ts) so it looks and
                // behaves like a link when rendered read-only elsewhere — but a click navigating
                // the whole tab away from the editor, discarding every unsaved change, is real
                // data loss. Clicking a link here should place the cursor/select the node, the
                // same as Google Docs or Word, not follow it.
                onClickCapture={e => {
                  if ((e.target as HTMLElement).closest('a[data-cdm-hyperlink]')) e.preventDefault()
                }}
                // The editable element is only as tall as the text, so the margins and the empty space
                // below the last line belong to this div — clicking them used to do nothing at all, and
                // a one-line document meant aiming at that one line to start typing. A click that does
                // not land on the content itself gets the nearest sensible caret position instead (see
                // focusNearestPosition). mousedown rather than click, so the caret appears on the way
                // down the way it does everywhere else, and preventDefault so the browser does not
                // blur the editor we just focused.
                onMouseDown={e => {
                  if (e.button !== 0) return
                  if ((e.target as HTMLElement).closest('.ProseMirror')) return
                  if (focusNearestPosition(editor, e.clientX, e.clientY) !== 'none') e.preventDefault()
                }}
                style={{
                  width: pageWidthPx,
                  // The drag handle and popover position from viewport rects, which already include
                  // this transform, so neither needs to know the scale.
                  transform: scale < 1 ? `scale(${scale})` : undefined,
                  transformOrigin: 'top left',
                  minHeight: 400,
                  background: colors.surface,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
                  paddingTop: marginPx.top, paddingBottom: marginPx.bottom,
                  boxSizing: 'border-box',
                  // The whole page is now a click target for editing, so it should say so.
                  cursor: 'text',
                }}
              >
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ padding: spacing.sm, borderBottom: `1px solid ${colors.border}` }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
              Preview payload (JSON)
              <textarea
                value={payloadJson}
                onChange={e => setPayloadJson(e.target.value)}
                rows={4}
                spellCheck={false}
                style={{ padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.xs, fontFamily: 'monospace', outline: 'none', resize: 'vertical', boxSizing: 'border-box', width: '100%' }}
              />
            </label>
          </div>
          {((editorDocMeta?.localization?.locales.length ?? 0) > 0 || previewLocale) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.xs} ${spacing.sm}`, borderBottom: `1px solid ${colors.border}`, fontSize: fontSize.sm, color: colors.textSecondary }}>
              Preview language
              <select
                aria-label="Preview language"
                value={previewLocale}
                onChange={e => setPreviewLocale(e.target.value)}
                style={{ padding: '2px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm }}
              >
                <option value="">Default{editorDocMeta?.localization?.defaultLanguage ? ` (${editorDocMeta.localization.defaultLanguage})` : ''}</option>
                {(editorDocMeta?.localization?.locales ?? []).map(l => <option key={l.locale} value={l.locale}>{l.locale}</option>)}
              </select>
            </label>
          )}
          <PreviewDebounceBar
            pending={previewPending}
            runKey={editVersion}
            durationMs={PREVIEW_DEBOUNCE_MS}
          />
          <div style={{ flex: 1, minHeight: 0 }}>
            <PreviewPane pdfUrl={pdfUrl} loading={previewLoading} error={previewError} />
          </div>
        </div>
      </div>
    </div>
  )
}
